import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, Trophy, Shield, User as UserIcon, Image as ImageIcon } from 'lucide-react';
import { categorie as categorieMock } from '../data/mock';
import SocialShare from '../components/SocialShare';
import { api } from '../api/client';
import { useSiteText } from '../hooks/useSiteText';

export default function Squadre() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCat = searchParams.get('cat') || categorieMock[0].key;
  const [active, setActive] = useState(initialCat);
  const [expanded, setExpanded] = useState(null);
  // categorie = metadati (key/label/colorAccent) da mock + squadre caricate dal backend
  const [categorie, setCategorie] = useState(categorieMock);

  useEffect(() => {
    if (searchParams.get('cat') && searchParams.get('cat') !== active) {
      setActive(searchParams.get('cat'));
    }
  }, [searchParams, active]);

  // Carica le squadre dal backend per ogni categoria (con fallback al mock)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const results = await Promise.all(
          categorieMock.map((c) => api.listTeams(c.key).catch(() => null))
        );
        if (!alive) return;
        const merged = categorieMock.map((c, i) => {
          const rows = Array.isArray(results[i]) ? results[i] : null;
          if (!rows || rows.length === 0) return c; // fallback al mock
          return { ...c, squadre: rows.map((t) => ({ ...t, id: t.id ?? `db-${Math.random()}` })) };
        });
        setCategorie(merged);
      } catch { /* mantieni mock */ }
    })();
    return () => { alive = false; };
  }, []);

  const handleTab = (key) => {
    setActive(key);
    setSearchParams({ cat: key });
    setExpanded(null);
  };

  const current = categorie.find((c) => c.key === active) || categorie[0];
  const title = useSiteText('squadre.title', 'Squadre Partecipanti');
  const subtitle = useSiteText('squadre.subtitle', '24 squadre · 3 categorie · XIV Memorial Eugenio Abbro');
  const formulaTitle = useSiteText('squadre.formula_title', 'Formula del Torneo');
  const formulaDesc = useSiteText('squadre.formula_desc', '2 gironi da 4 squadre per ogni categoria · gare di sola andata · le prime due classificate di ciascun girone disputano semifinali e finale. Partite 7 vs 7 su campi ridotti, 2 tempi da 20 minuti.');

  return (
    <div className="bg-navy min-h-screen">
      <section className="relative py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-navy-dark to-navy"></div>
        <div className="relative max-w-5xl mx-auto px-4 lg:px-8 text-center">
          <Trophy size={44} className="text-pink-brand mx-auto mb-3" />
          <h1 className="font-brand text-5xl md:text-6xl font-bold text-white mb-3">{title}</h1>
          <div className="w-24 h-1 bg-pink-brand mx-auto rounded"></div>
          <p className="text-white/70 mt-5 max-w-2xl mx-auto">
            {subtitle}
          </p>
          <div className="mt-5 flex justify-center">
            <SocialShare title="Squadre XIV Memorial Eugenio Abbro" />
          </div>
        </div>
      </section>

      <section className="pb-6">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <div className="flex flex-wrap gap-2 bg-navy-dark p-1.5 rounded-2xl border border-white/10">
            {categorie.map((c) => (
              <button
                key={c.key}
                onClick={() => handleTab(c.key)}
                className={`flex-1 min-w-[140px] py-3 px-4 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-wider transition-all ${
                  active === c.key ? 'bg-pink-brand text-white shadow-lg' : 'text-white/70 hover:text-white'
                }`}
              >
                {c.label}
                <span className="block text-[10px] font-semibold opacity-70 mt-0.5">{c.squadre.length} squadre</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gironi info */}
      <section className="pb-4">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          <div className="grid sm:grid-cols-2 gap-4">
            {['A', 'B'].map((g) => {
              const teams = current.squadre.filter((s) => s.girone === g);
              return (
                <div key={g} className="glass-card p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-9 h-9 rounded-lg bg-pink-brand text-white font-bold flex items-center justify-center">{g}</span>
                    <h3 className="text-white font-bold uppercase tracking-wider">Girone {g}</h3>
                    <span className="ml-auto text-white/60 text-xs">{teams.length} squadre</span>
                  </div>
                  <ul className="space-y-1">
                    {teams.map((t) => (
                      <li key={t.id} className="flex items-center gap-2 text-white/85 text-sm py-1">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }}></span>
                        {t.name}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cards squadre con rosa */}
      <section className="pb-16 pt-6">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <Users size={22} className="text-pink-brand" />
            <h2 className="font-display text-2xl text-white uppercase font-bold">
              {current.label} · Tutte le squadre
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {current.squadre.map((s) => {
              const isOpen = expanded === s.id;
              return (
                <div key={s.id} className="glass-card overflow-hidden">
                  {/* Foto squadra */}
                  <div className="relative aspect-[16/9] overflow-hidden bg-navy-dark">
                    {s.foto ? (
                      <img src={s.foto} alt={s.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/30">
                        <ImageIcon size={40} />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-dark via-navy-dark/30 to-transparent"></div>
                    <div className="absolute bottom-3 left-4 right-4 flex items-end gap-3">
                      <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-brand font-bold text-lg flex-shrink-0 shadow-lg" style={{ backgroundColor: s.color }}>
                        {s.name.replace('A.S.D.', '').trim().substring(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-bold text-base leading-tight drop-shadow">{s.name}</h3>
                        <div className="mt-1 inline-flex items-center gap-1 bg-pink-brand/90 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                          <Shield size={10} /> Girone {s.girone}
                        </div>
                      </div>
                      <SocialShare title={s.name} compact />
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-5 space-y-3 text-sm">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="flex items-start gap-2">
                        <UserIcon size={14} className="text-pink-brand mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="text-white/50 font-bold uppercase tracking-widest text-[10px]">Allenatore</div>
                          <div className="text-white/90">{s.allenatore || s.allenatori}</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <UserIcon size={14} className="text-pink-brand mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="text-white/50 font-bold uppercase tracking-widest text-[10px]">Dirigente</div>
                          <div className="text-white/90">{s.dirigente}</div>
                        </div>
                      </div>
                    </div>

                    {s.giocatori && (
                      <div className="pt-2 border-t border-white/10">
                        <button
                          onClick={() => setExpanded(isOpen ? null : s.id)}
                          className="flex items-center justify-between w-full text-pink-brand text-xs font-bold uppercase tracking-widest hover:text-white transition-colors"
                        >
                          <span>Rosa giocatori</span>
                          <span>{isOpen ? '−' : '+'}</span>
                        </button>
                        {isOpen && (
                          <div className="mt-3 bg-navy-dark rounded-lg p-4 animate-fade-in">
                            <p className="text-white/85 text-sm leading-relaxed">{s.giocatori}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          <div className="glass-card p-6 text-center">
            <Trophy size={28} className="text-pink-brand mx-auto mb-3" />
            <h3 className="text-white font-bold text-lg mb-2">{formulaTitle}</h3>
            <p className="text-white/75 text-sm max-w-2xl mx-auto">
              {formulaDesc}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
